import { inject, Injectable } from '@angular/core'
import { FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import moment from 'moment'
import { ProductionTechnology } from '../models/production-technology.enum'
import { MarketingType } from '../models/marketing-type.enum'
import { BatteryStorageStatus, BatteryStorageUsage } from '../models/battery-storage-status.enum'
import { ContractPartner } from '../models/contract-partner.enum'
import { SolarResourceFormControls, WindResourceFormControls } from '../models/technical-resource'
import { ContractDateService } from './contract-date.service'
import {
  ContractAdjustmentOption,
  SubsidizedPeriodModalComponent,
  SubsidizedPeriodModalData,
} from '../../technical-data/technical-resource-data/subsidized-period-modal/subsidized-period-modal.component'
import { MatDialog } from '@angular/material/dialog'
import { ContactFormData, GeneralFormData, PriceFormControls } from '../models/main-form'
import { ServiceFeeUnit } from '../models/price'
import {
  DIGITS_NON_LEADING_ZERO_PATTERN,
  EMAIL_PATTERN,
  NAME_PATTERN,
  PHONE_PATTERN,
  POSTAL_CODE_PATTERN,
} from '../validators/validator-pattern'
import { CustomValidators } from '../validators/custom-validators'
import { NotificationService } from '../components/notification/notification.service'
import { CONTACT_EMAIL } from '../../constants'
import { RegisterType, RegistrationAuthorityName } from '../models/commercial-register-data'
import { environment } from '../../../environments/environment'

export type GeneralFormControls = {
  [K in Exclude<keyof GeneralFormData, 'technicalResources'>]: FormControl<GeneralFormData[K]>
} & {
  technicalResources: FormArray<FormGroup<WindResourceFormControls | SolarResourceFormControls>>
}

export type ContactFormControls = {
  [K in keyof ContactFormData]: FormControl<ContactFormData[K]>
}

@Injectable({ providedIn: 'root' })
export class OfferFormService {
  generalData: FormGroup<GeneralFormControls>
  contactData: FormGroup<ContactFormControls>
  priceData: FormGroup<PriceFormControls>

  private readonly dialog: MatDialog = inject(MatDialog)
  private readonly contractDateService = inject(ContractDateService)
  private readonly notificationService = inject(NotificationService)
  private readonly SUBSIDIZED_PERIOD_ERROR = 'subsidizingPeriodInvalid'
  private subsidizedPeriodModalOpened = false

  constructor() {
    const queryParam = new URLSearchParams(window.location.search).get('technology')
    const productionTechnology = Object.values(ProductionTechnology).find((value) => value === queryParam) || null
    this.generalData = new FormGroup<GeneralFormControls>(
      {
        technicalResources: new FormArray<FormGroup<WindResourceFormControls | SolarResourceFormControls>>([]),
        locationNameCustomer: new FormControl<string | null>('', {
          validators: [Validators.maxLength(50)],
          updateOn: 'blur',
        }),
        productionTechnology: new FormControl<ProductionTechnology | null>(productionTechnology, [Validators.required]),
        isAlreadyInOperation: new FormControl<boolean | null>(null, [Validators.required]),
        marketingType: new FormControl<MarketingType | null>(null, [Validators.required]),
        contractStartDate: new FormControl<moment.Moment | null>(null, [Validators.required]),
        contractEndDate: new FormControl<moment.Moment | null>(null, [Validators.required]),
        totalInstalledCapacity: new FormControl<number | null>(null),
        plannedProductionPerYear: new FormControl<number | null>(null, [
          Validators.required,
          Validators.min(0),
          Validators.pattern(DIGITS_NON_LEADING_ZERO_PATTERN),
          CustomValidators.zeroIsNotAllowedAsFirstCharacter(),
        ]),
        ownConsumptionPercent: new FormControl<number | null>(null, {
          validators: [
            ...(environment.enableSmallDV ? [Validators.required] : []),
            Validators.min(0),
            Validators.max(100),
            Validators.pattern(DIGITS_NON_LEADING_ZERO_PATTERN),
          ],
          updateOn: 'blur',
        }),
        ownConsumptionKw: new FormControl<number | null>(null, {
          validators: [
            ...(environment.enableSmallDV ? [Validators.required] : []),
            Validators.min(0),
            Validators.pattern(DIGITS_NON_LEADING_ZERO_PATTERN),
          ],
          updateOn: 'blur',
        }),
        feedInPerYear: new FormControl<number | null>(null),
        batteryStorageStatus: new FormControl<BatteryStorageStatus | null>(null, [Validators.required]),
        batteryStorageUsages: new FormControl<BatteryStorageUsage[]>([]),
      },
      {
        validators: [this.subsidizedPeriodValidator],
      }
    )

    this.contactData = new FormGroup<ContactFormControls>({
      contractPartner: new FormControl<ContractPartner | null>(null, [Validators.required]),
      // Company fields
      contractOwnerName: new FormControl<string | null>(''),
      registrationType: new FormControl<RegisterType | null>(null),
      registrationNumber: new FormControl<string | null>(''),
      registrationAuthorityName: new FormControl<RegistrationAuthorityName | null>(null),
      // Person fields
      firstName: new FormControl<string | null>(''),
      lastName: new FormControl<string | null>(''),
      // Common fields
      addressPostalCode: new FormControl<string | null>('', {
        validators: [Validators.required, Validators.pattern(POSTAL_CODE_PATTERN)],
        updateOn: 'blur',
      }),
      addressCity: new FormControl<string | null>('', [Validators.required]),
      addressStreet: new FormControl<string | null>('', { validators: [Validators.required], updateOn: 'blur' }),
      addressHouseNumber: new FormControl<string | null>('', {
        validators: [Validators.required],
        updateOn: 'blur',
      }),
      contactInfoFirstName: new FormControl<string | null>('', {
        validators: [Validators.required, Validators.maxLength(50), Validators.pattern(NAME_PATTERN)],
      }),
      contactInfoLastName: new FormControl<string | null>('', {
        validators: [Validators.required, Validators.maxLength(100), Validators.pattern(NAME_PATTERN)],
        updateOn: 'blur',
      }),
      contactInfoPhone: new FormControl<string | null>('', {
        validators: [Validators.required, Validators.maxLength(20), Validators.pattern(PHONE_PATTERN)],
        updateOn: 'blur',
      }),
      contactInfoEmail: new FormControl<string | null>('', {
        validators: [Validators.required, Validators.pattern(EMAIL_PATTERN)],
        updateOn: 'blur',
      }),
      dataProtectionAccepted: new FormControl<boolean | null>(false, [Validators.requiredTrue]),
    })

    this.priceData = new FormGroup<PriceFormControls>({
      price: new FormControl<number | null>(null, [Validators.required]),
      pricingId: new FormControl<string | null>(null),
      serviceFeeUnit: new FormControl<ServiceFeeUnit>('EUR/MWh', { nonNullable: true }),
    })
    this.generalData.statusChanges.subscribe(() => this.checkSubsidizedPeriod())
    this.generalData.valueChanges.subscribe(() => {
      if (this.priceData.valid) {
        this.priceData.controls.price.addValidators(Validators.required)
        this.priceData.reset()
      }
    })
  }

  checkSubsidizedPeriod() {
    if (!this.subsidizedPeriodModalOpened && this.generalData.hasError(this.SUBSIDIZED_PERIOD_ERROR)) {
      this.subsidizedPeriodModalOpened = true
      this.showSubsidizedModal()
    }
  }

  checkCommissioningDateAgainstOperationStatus() {
    const invalidResourceIndex = this.generalData?.controls.technicalResources.controls.findIndex((resource) =>
      this.isMarketRegisterCommissioningDateInvalid(resource)
    )
    if (invalidResourceIndex >= 0) {
      this.showCommissioningDateGlobalError(
        this.generalData.controls.isAlreadyInOperation.value || false,
        invalidResourceIndex === 0
      )
    }
  }

  showCommissioningDateGlobalError(inOperation: boolean, singleResource: boolean) {
    if (inOperation) {
      const title = singleResource
        ? 'Widerspruch bei geplanter Inbetriebnahme'
        : 'Einzelne Stromerzeugungseinheit nicht in Betrieb'
      const description = singleResource
        ? 'Die Anlage wurde als „in Betrieb“ angegeben, laut Marktstammdatenregister liegt jedoch nur ein geplantes Inbetriebnahmedatum vor. Bitte überprüfen Sie den Betriebsstatus der Anlage.'
        : 'Die Anlage wurde als „in Betrieb“ angegeben, jedoch ist mindestens eine der zugeordneten Stromerzeugungseinheiten laut Marktstammdatenregister noch nicht in Betrieb. Bitte prüfen Sie den Betriebsstatus der einzelnen Ressourcen.'
      this.notificationService.error(title, description)
    } else {
      const title = singleResource
        ? 'Widerspruch bei bereits erfolgter Inbetriebnahme'
        : 'Einzelne Stromerzeugungseinheit bereits in Betrieb'
      const description = singleResource
        ? 'Die Anlage wurde als „nicht in Betrieb“ angegeben, laut Marktstammdatenregister ist jedoch bereits ein Datum der erstmaligen Inbetriebnahme hinterlegt. Bitte überprüfen Sie den Betriebsstatus der Anlage'
        : 'Die Anlage wurde als „nicht in Betrieb“ angegeben, jedoch ist mindestens eine der zugeordneten Stromerzeugungseinheiten laut Marktstammdatenregister bereits in Betrieb. Bitte prüfen Sie den Betriebsstatus der einzelnen Ressourcen.'
      this.notificationService.error(title, description)
    }
  }

  isMarketRegisterCommissioningDateInvalid(resource: FormGroup<SolarResourceFormControls | WindResourceFormControls>) {
    return (
      (!!resource.controls.commissioningDateCommon.errors?.['dateInPastNotAllowed'] ||
        !!resource.controls.commissioningDateCommon.errors?.['dateInFutureNotAllowed']) &&
      !!resource.controls.mastrIdGenerationUnit.value
    )
  }

  private showSubsidizedModal() {
    const resourceIndex: number = this.generalData.getError(this.SUBSIDIZED_PERIOD_ERROR)['index']
    const commissioningDate = this.generalData.controls.technicalResources
      .at(resourceIndex)
      .get('commissioningDateCommon')!.value!

    const validEndDate = this.contractDateService.findEndDateInSubsidizedPeriod(moment(commissioningDate))
    const validStartDate = this.contractDateService.findStartDateOutsideSubsidizedPeriod(
      moment(commissioningDate),
      moment(this.generalData.controls.contractStartDate.value)
    )
    const data: SubsidizedPeriodModalData = {
      contractStartDate: this.generalData.controls.contractStartDate.value!,
      validContractStartDate: validStartDate,
      contractEndDate: this.generalData.controls.contractEndDate.value!,
      validContractEndDate: validEndDate,
    }
    this.dialog
      .open(SubsidizedPeriodModalComponent, { data, width: '640px' })
      .afterClosed()
      .subscribe((userInput: ContractAdjustmentOption) => {
        this.adjustContract(userInput, data)
        this.subsidizedPeriodModalOpened = false
      })
  }

  private adjustContract(userInput: ContractAdjustmentOption, data: SubsidizedPeriodModalData) {
    switch (userInput) {
      case ContractAdjustmentOption.ChangeMarketingType:
        this.generalData.controls.marketingType.setValue(MarketingType.Other)
        break
      case ContractAdjustmentOption.AdjustEndDate:
        this.generalData.controls.contractEndDate.setValue(data.validContractEndDate || data.contractEndDate)
        break
      case ContractAdjustmentOption.AdjustStartDate:
        this.generalData.patchValue({
          marketingType: MarketingType.Other,
          contractStartDate: data.validContractStartDate || data.contractStartDate,
        })
        break
    }
  }

  subsidizedPeriodValidator: ValidatorFn = (): ValidationErrors | null => {
    if (!this.isSubsidizedPlant()) {
      return null
    }
    const invalidResourceIndex: number = this.generalData.controls.technicalResources.controls
      .map((control) => control as FormGroup<SolarResourceFormControls>)
      .map((resource) => resource.controls.commissioningDateCommon.value)
      .filter((commissioningDate) => !!commissioningDate)
      .findIndex(
        (commissioningDate) =>
          !ContractDateService.isEndDateInSubsidizedPeriod(
            moment(commissioningDate),
            moment(this.generalData.controls.contractEndDate.value)
          )
      )
    return invalidResourceIndex >= 0 ? { [this.SUBSIDIZED_PERIOD_ERROR]: { index: invalidResourceIndex } } : null
  }

  private isSubsidizedPlant(): boolean {
    return (
      this.generalData?.controls.marketingType.value === MarketingType.EEGDirectMarketing &&
      this.generalData?.controls.isAlreadyInOperation.value === true
    )
  }

  checkTotalInstalledCapacity() {
    if (
      !!this.generalData.controls.totalInstalledCapacity.value &&
      this.generalData.controls.totalInstalledCapacity.errors
    ) {
      if (this.generalData.controls.productionTechnology.value === ProductionTechnology.Solar) {
        this.notificationService.error(
          'Gerne erstellen wir Ihnen ein passendes Angebot',
          `<div><p>Aktuell bieten wir Direktvermarktungsangebote für Solaranlagen mit einer installierten Leistung über <b>5 MW bis 10 MW</b> an.</p><p>Für Anlagen <b>bis 5 MW</b> können Sie Ihr Angebot bequem über <a href="https://www.interconnector.de/direktvermarktung-strom/direktvermarktungsangebot-anfordern/" target="_blank">Interconnector</a> anfragen.</p><p>Für Anlagen <b>über 10 MW</b> erstellen wir Ihnen ein individuelles Angebot. Bitte laden Sie das <a href="/assets/docs/2025_EnBW_Stammdatentemplate_Direktvermarktung-Indikation_Wind_PV.xltx" target="_blank">Stammdatenblatt</a> herunter und senden Sie dieses an <a href="mailto:${CONTACT_EMAIL}">${CONTACT_EMAIL}</a>.</p><p>Wir bedanken uns für Ihr Verständnis.</p></div>`
        )
      } else if (this.generalData.controls.productionTechnology.value === ProductionTechnology.WindOnshore) {
        this.notificationService.error(
          'Gerne erstellen wir Ihnen ein passendes Angebot',
          `<div><p>Die Online-Anfrage ist aktuell für Wind-Onshore-Anlagen mit einer installierten Leistung von <b>mehr als 1 MW bis 10 MW</b> vorgesehen.</p><p>Für Anlagen <b>bis 1 MW</b> können Sie Ihr Angebot bequem über <a href="https://www.interconnector.de/direktvermarktung-strom/direktvermarktungsangebot-anfordern/" target="_blank">Interconnector</a> anfragen.</p><p>Für Anlagen <b>über 10 MW</b> erstellen wir Ihnen ein individuelles Angebot. Bitte laden Sie das <a href="/assets/docs/2025_EnBW_Stammdatentemplate_Direktvermarktung-Indikation_Wind_PV.xltx" target="_blank">Stammdatenblatt</a> herunter und senden Sie dieses an <a href="mailto:${CONTACT_EMAIL}">${CONTACT_EMAIL}</a>.</p><p>Wir bedanken uns für Ihr Verständnis.</p></div>`
        )
      }
    }
  }
}
